import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { tendersAPI } from "../services/api";
import { Search, Filter, Download, X, BookMarked, ChevronLeft, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";

const CATEGORIES = ["Oil & Gas", "Construction", "Healthcare", "Information Technology", "Agriculture", "Energy", "Security Services", "Legal Services", "Catering & Hospitality", "Telecommunications", "Environment", "Water & Sanitation", "Humanitarian"];
const STATES = ["Federal", "Rivers", "Lagos", "FCT", "Delta", "Kano", "Anambra", "Ogun", "Oyo", "Kaduna", "Borno", "Enugu", "Bayelsa"];
const URGENCIES = ["critical", "urgent", "soon", "normal"];

// Every filter the page can send, all blank. search() drops blank values, so
// a filter nobody touched never reaches the API. clearFilters() and the
// initial state both start from this one object so they cannot drift apart.
const EMPTY_FILTERS = {
  keyword: "", category: "", state: "", urgency: "",
  min_value: "", max_value: "", sort_by: "deadline",
  published_from: "", published_to: "", deadline_from: "", deadline_to: "",
  currency: "", source_id: "", tender_type: "", procurement_method: "",
  industry: "", country: "", eligibility: "",
};
// Dropdown contents come from GET /tenders/filters/options. Until it answers
// (or if it fails) every list is empty and the dependent dropdowns stay hidden.
const EMPTY_OPTIONS = {
  sources: [], currencies: [], tender_types: [], procurement_methods: [], industries: [], countries: [],
};
const FALLBACK_CURRENCIES = ["NGN", "USD", "EUR", "GBP"];
const LABEL_STYLE = { fontSize: "11px", color: "var(--text-muted)", marginBottom: "4px", display: "block" };
const CHIP_STYLE = { background: "rgba(255,255,255,0.05)", color: "var(--text-secondary)", padding: "2px 8px", borderRadius: "100px", fontSize: "11px" };
const labelFor = (list, value) => (list.find(o => o.value === value) || {}).label || value;

export default function AdvancedSearch() {
  const navigate = useNavigate();
  const [tenders, setTenders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [options, setOptions] = useState(EMPTY_OPTIONS);

  const update = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({ ...EMPTY_FILTERS });
  const activeFilters = Object.entries(filters).filter(([k, v]) => v && k !== "sort_by").length;

  const search = async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: 15, status: "open", ...filters };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const res = await tendersAPI.list(params);
      setTenders(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
      setPage(p);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Search failed");
    }
    finally { setLoading(false); }
  };

  // Intentionally mount-only: search() is a plain function (not memoized),
  // so listing it here would re-run this effect — and re-fetch — on every
  // render. Subsequent searches are triggered explicitly by the Apply
  // button, Enter key, and pagination controls below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { search(); }, []);

  // Dropdown values for the extra filters. Optional: if this fails (for
  // example the API is mid-deploy) the page still searches; the dependent
  // dropdowns simply stay hidden.
  useEffect(() => {
    let cancelled = false;
    tendersAPI.filterOptions()
      .then(res => { if (!cancelled) setOptions({ ...EMPTY_OPTIONS, ...res.data }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const selectAll = () => setSelected(tenders.map(t => t.id));
  const clearSelection = () => setSelected([]);

  const exportToCSV = () => {
    const toExport = selected.length > 0 ? tenders.filter(t => selected.includes(t.id)) : tenders;
    const headers = ["ID", "Title", "Agency", "Ministry", "Category", "State", "Reference", "Contract Value", "Currency", "Status", "Urgency", "Days Remaining", "Publication Date", "Submission Deadline", "Requirements", "Eligibility", "Source URL"];
    const rows = toExport.map(t => [
      t.id, `"${t.title}"`, `"${t.agency}"`, `"${t.ministry}"`, t.category, t.state,
      t.reference_number, t.contract_value || "", t.currency, t.status, t.urgency,
      t.days_remaining,
      t.publication_date ? new Date(t.publication_date).toLocaleDateString("en-NG") : "",
      t.submission_deadline ? new Date(t.submission_deadline).toLocaleDateString("en-NG") : "",
      `"${t.requirements || ""}"`, `"${t.eligibility || ""}"`, t.source_url || ""
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `protrackng_tenders_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${toExport.length} tenders to CSV`);
  };

  const handleSave = async (id, e) => {
    e.stopPropagation();
    try {
      const res = await tendersAPI.save(id);
      setTenders(prev => prev.map(t => t.id === id ? { ...t, is_saved: res.data.saved } : t));
      toast.success(res.data.message);
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px" }}>
        <div>
          <h1 style={{ fontSize: "26px", marginBottom: "6px" }}>Advanced Search</h1>
          <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>{total} tenders found</p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {selected.length > 0 && (
            <span style={{ padding: "8px 14px", background: "var(--accent-green-dim)", color: "var(--accent-green)", borderRadius: "8px", fontSize: "13px", fontWeight: "600" }}>
              {selected.length} selected
            </span>
          )}
          <button className="btn btn-primary" onClick={exportToCSV} style={{ background: "var(--accent-blue)" }}>
            <Download size={15} /> Export {selected.length > 0 ? `(${selected.length})` : "All"} to CSV
          </button>
          <button className="btn btn-ghost" onClick={() => setShowFilters(f => !f)}>
            <Filter size={15} /> Filters {activeFilters > 0 && <span style={{ background: "var(--accent-orange)", color: "#fff", borderRadius: "100px", padding: "1px 6px", fontSize: "11px" }}>{activeFilters}</span>}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="card" style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h3 style={{ fontSize: "14px", color: "var(--text-secondary)" }}>SEARCH FILTERS</h3>
            {activeFilters > 0 && <button onClick={clearFilters} style={{ background: "none", border: "none", color: "var(--accent-red)", cursor: "pointer", fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}><X size={12} /> Clear All</button>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div style={{ position: "relative" }}>
              <Search size={14} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
              <input value={filters.keyword} onChange={e => update("keyword", e.target.value)} placeholder="Keywords..." style={{ paddingLeft: "32px" }} onKeyDown={e => e.key === "Enter" && search()} />
            </div>
            <select value={filters.category} onChange={e => update("category", e.target.value)}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.state} onChange={e => update("state", e.target.value)}>
              <option value="">All States</option>
              {STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filters.urgency} onChange={e => update("urgency", e.target.value)}>
              <option value="">All Urgencies</option>
              {URGENCIES.map(u => <option key={u} value={u} style={{ textTransform: "capitalize" }}>{u.charAt(0).toUpperCase() + u.slice(1)}</option>)}
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "12px", alignItems: "end" }}>
            <div>
              <label style={LABEL_STYLE}>Min Value{filters.currency ? ` (${filters.currency})` : ""}</label>
              <input type="number" value={filters.min_value} onChange={e => update("min_value", e.target.value)} placeholder="e.g. 50000000" />
            </div>
            <div>
              <label style={LABEL_STYLE}>Max Value{filters.currency ? ` (${filters.currency})` : ""}</label>
              <input type="number" value={filters.max_value} onChange={e => update("max_value", e.target.value)} placeholder="e.g. 5000000000" />
            </div>
            <select value={filters.sort_by} onChange={e => update("sort_by", e.target.value)}>
              <option value="deadline">Sort: Deadline</option>
              <option value="value_high">Sort: Value High</option>
              <option value="value_low">Sort: Value Low</option>
              <option value="newest">Sort: Newest</option>
            </select>
            <button className="btn btn-primary" onClick={() => search(1)} style={{ padding: "10px 20px" }}>
              <Search size={14} /> Search
            </button>
          </div>
          <div style={{ borderTop: "1px solid var(--border)", marginTop: "16px", paddingTop: "14px" }}>
            <h3 style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "10px", letterSpacing: "0.5px" }}>MORE FILTERS</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "12px" }}>
              <div>
                <label style={LABEL_STYLE}>Published from</label>
                <input type="date" value={filters.published_from} max={filters.published_to || undefined} onChange={e => update("published_from", e.target.value)} />
              </div>
              <div>
                <label style={LABEL_STYLE}>Published to</label>
                <input type="date" value={filters.published_to} min={filters.published_from || undefined} onChange={e => update("published_to", e.target.value)} />
              </div>
              <div>
                <label style={LABEL_STYLE}>Deadline from</label>
                <input type="date" value={filters.deadline_from} max={filters.deadline_to || undefined} onChange={e => update("deadline_from", e.target.value)} />
              </div>
              <div>
                <label style={LABEL_STYLE}>Deadline to</label>
                <input type="date" value={filters.deadline_to} min={filters.deadline_from || undefined} onChange={e => update("deadline_to", e.target.value)} />
              </div>
              <div>
                <label style={LABEL_STYLE}>Currency</label>
                <select value={filters.currency} onChange={e => update("currency", e.target.value)}>
                  <option value="">Any currency</option>
                  {(options.currencies.length ? options.currencies : FALLBACK_CURRENCIES).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {options.sources.length > 0 && (
                <div>
                  <label style={LABEL_STYLE}>Source</label>
                  <select value={filters.source_id} onChange={e => update("source_id", e.target.value)}>
                    <option value="">All sources</option>
                    {options.sources.map(src => <option key={src.id} value={src.id}>{src.name}</option>)}
                  </select>
                </div>
              )}
              {options.tender_types.length > 0 && (
                <div>
                  <label style={LABEL_STYLE}>Tender type</label>
                  <select value={filters.tender_type} onChange={e => update("tender_type", e.target.value)}>
                    <option value="">All types</option>
                    {options.tender_types.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              )}
              {options.procurement_methods.length > 0 && (
                <div>
                  <label style={LABEL_STYLE}>Procurement method</label>
                  <select value={filters.procurement_method} onChange={e => update("procurement_method", e.target.value)}>
                    <option value="">All methods</option>
                    {options.procurement_methods.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              )}
              {options.industries.length > 0 && (
                <div>
                  <label style={LABEL_STYLE}>Industry</label>
                  <select value={filters.industry} onChange={e => update("industry", e.target.value)}>
                    <option value="">All industries</option>
                    {options.industries.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
              )}
              {options.countries.length > 0 && (
                <div>
                  <label style={LABEL_STYLE}>Country</label>
                  <select value={filters.country} onChange={e => update("country", e.target.value)}>
                    <option value="">All countries</option>
                    {options.countries.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label style={LABEL_STYLE}>Eligibility mentions</label>
                <input value={filters.eligibility} onChange={e => update("eligibility", e.target.value)} placeholder="e.g. CAC, NSITF" onKeyDown={e => e.key === "Enter" && search()} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={selectAll} style={{ background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "13px" }}>Select All</button>
          {selected.length > 0 && <button onClick={clearSelection} style={{ background: "none", border: "none", color: "var(--accent-red)", cursor: "pointer", fontSize: "13px" }}>Clear</button>}
        </div>
        <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>{tenders.length} of {total} shown</span>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px", color: "var(--text-muted)" }}>Searching...</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {tenders.map(tender => {
            const isSelected = selected.includes(tender.id);
            const urgencyColors = { critical: "var(--accent-red)", urgent: "var(--accent-orange)", soon: "var(--accent-blue)", normal: "var(--accent-green)" };
            const color = urgencyColors[tender.urgency] || "var(--accent-green)";
            return (
              <div key={tender.id} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px", background: isSelected ? "rgba(0,212,161,0.05)" : "var(--bg-card)", border: `1px solid ${isSelected ? "var(--accent-green)" : "var(--border)"}`, borderLeft: `3px solid ${color}`, borderRadius: "12px", cursor: "pointer", transition: "all 0.15s" }}
                onClick={() => navigate(`/tenders/${tender.id}`)}>
                <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(tender.id)} onClick={e => e.stopPropagation()}
                  style={{ width: "16px", height: "16px", accentColor: "var(--accent-green)", cursor: "pointer", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: "6px", marginBottom: "6px", flexWrap: "wrap" }}>
                    {tender.days_remaining != null && (
                      <span style={{ background: `${color}20`, color, padding: "2px 8px", borderRadius: "100px", fontSize: "11px", fontWeight: "600" }}>{tender.days_remaining}d left</span>
                    )}
                    <span style={CHIP_STYLE}>{tender.category}</span>
                    <span style={CHIP_STYLE}>{tender.state}</span>
                    {tender.tender_type && <span style={CHIP_STYLE}>{labelFor(options.tender_types, tender.tender_type)}</span>}
                    {tender.procurement_method && <span style={CHIP_STYLE}>{labelFor(options.procurement_methods, tender.procurement_method)}</span>}
                    {/* Nigeria is the implicit default for this product, so only flag tenders from elsewhere. */}
                    {tender.country && tender.country.toLowerCase() !== "nigeria" && <span style={CHIP_STYLE}>{tender.country}</span>}
                  </div>
                  <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tender.title}</div>
                  <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{tender.agency} • {tender.reference_number}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: "15px", fontWeight: "700", color: "var(--accent-green)", fontFamily: "Syne" }}>{tender.contract_value_formatted}</div>
                </div>
                <button className="btn btn-ghost" style={{ padding: "6px 10px", flexShrink: 0 }} onClick={e => handleSave(tender.id, e)}>
                  <BookMarked size={13} color={tender.is_saved ? "var(--accent-green)" : undefined} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", marginTop: "24px" }}>
          <button className="btn btn-ghost" onClick={() => search(page - 1)} disabled={page === 1}><ChevronLeft size={16} /> Prev</button>
          <span style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Page {page} of {totalPages}</span>
          <button className="btn btn-ghost" onClick={() => search(page + 1)} disabled={page === totalPages}>Next <ChevronRight size={16} /></button>
        </div>
      )}
    </div>
  );
}
